"use client";

import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { useGetMyOrdersQuery } from "@/features/api/paymentApi";
import { useGetCourseProgressQuery } from "@/features/api/courseApi";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { BookOpen, Video, CheckCircle } from "lucide-react";

export default function MyCoursesPage() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  /* ✅ ONLY ORDERS */
  const { data, isLoading } = useGetMyOrdersQuery(undefined, { skip: !user });

  const orders = (data?.data || []).filter(
  (o) => o.status === "SUCCESS" && o.productType === "course"
);

  /* ---------------- PROGRESS MAP ---------------- */
  const { data: progressRes } = useGetCourseProgressQuery(undefined, {
    skip: !orders.length,
  });

  const progressMap = useMemo(() => {
    const map = {};
    progressRes?.forEach((p) => (map[p.courseId] = p));
    return map;
  }, [progressRes]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 p-10">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-64" />
        ))}
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        You haven’t enrolled in any courses yet.
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-10">

        <h1 className="text-3xl font-bold text-center mb-10">🎓 My Courses</h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">

          {orders.map((o) => {
            const course = o.course;
            if (!course) return null;

            const progress = progressMap[course._id] || {};
            const percentage = progress.percentage || 0;

            return (
              <Card key={course._id} className="flex flex-col">

                {percentage === 100 && (
                  <div className="absolute top-2 right-2 bg-green-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Completed
                  </div>
                )}

                {course.featuredImage ? (
                  <img
                    src={course.featuredImage}
                    className="h-40 w-full object-cover"
                  />
                ) : (
                  <div className="h-40 flex items-center justify-center bg-muted">
                    <Video className="h-10 w-10" />
                  </div>
                )}

                <CardHeader>
                  <h2 className="font-semibold">{course.title}</h2>
                </CardHeader>

                <CardContent className="mt-auto space-y-3">

                  <Progress value={percentage} />

                  <Button
                    onClick={() => navigate(`/courses/${course.slug}/learn`)}
                    className="w-full"
                  >
                    <BookOpen className="mr-2 h-4 w-4" />
                    {percentage > 0 ? "Resume" : "Start"}
                  </Button>

                </CardContent>
              </Card>
            );
          })}

        </div>
      </div>
    </div>
  );
}